import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, AlertCircle, TrendingUp, Calendar, PieChart, Shield, Users, FileText, BarChart2, CreditCard, Zap } from 'lucide-react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { SeoHead } from '../components/SeoHead';

export function GestionTresoreriePMEPage() {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/login');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Pourquoi la trésorerie est-elle critique pour une PME ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Une PME peut être rentable sur le papier et pourtant se retrouver en cessation de paiement faute de liquidités suffisantes. Les délais de paiement clients (30 à 90 jours) créent un décalage entre le moment où vous livrez et celui où vous encaissez. Pendant ce temps, les fournisseurs, salariés et charges sociales continuent d'exiger des paiements ponctuels. La trésorerie est donc le vrai indicateur de survie d'une PME."
        }
      },
      {
        "@type": "Question",
        "name": "Quelle est la différence entre résultat comptable et trésorerie ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le résultat comptable mesure la rentabilité : recettes moins dépenses sur une période. La trésorerie mesure la liquidité : l'argent réellement disponible en banque. Une PME peut afficher un bénéfice comptable de 50 000 € tout en ayant un compte bancaire négatif si ses clients n'ont pas encore payé leurs factures. C'est le fameux paradoxe : 'je suis rentable mais je n'ai pas d'argent'."
        }
      },
      {
        "@type": "Question",
        "name": "Comment calculer le besoin en fonds de roulement (BFR) d'une PME ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le BFR = Stocks + Créances clients - Dettes fournisseurs. Pour une PME de services avec 500 000 € de CA annuel, des délais clients de 45 jours et des délais fournisseurs de 30 jours : BFR = (500 000 / 365 × 45) - (dépenses / 365 × 30). Réduire les délais clients de 15 jours peut libérer 20 000 € de trésorerie sans aucun financement externe."
        }
      },
      {
        "@type": "Question",
        "name": "Quel outil utiliser pour gérer la trésorerie d'une PME ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plusieurs options : tableur Excel (gratuit, chronophage, risque d'erreurs), logiciel comptable (vision passée, pas prévisionnelle), ou outil spécialisé comme TrezoOne (plan de trésorerie automatisé, alertes, simulations de scénarios). Pour une PME avec plusieurs clients et fournisseurs, un outil dédié devient rapidement indispensable pour anticiper les tensions et prendre des décisions éclairées."
        }
      },
      {
        "@type": "Question",
        "name": "Comment améliorer la trésorerie d'une PME rapidement ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Actions rapides : relancer les factures impayées dès J+1 du délai (peut libérer 15 à 30% de CA en attente), proposer des escomptes pour paiement anticipé (2% pour paiement à 10 jours), négocier des délais fournisseurs plus longs, mettre en place des acomptes sur les nouveaux contrats, optimiser les stocks. Actions structurelles : affacturage, ligne de trésorerie bancaire, optimisation du cycle de facturation."
        }
      }
    ]
  };

  return (
    <>
      <SeoHead
        title="Gestion trésorerie PME : outil de pilotage et prévisionnel"
        description="Pilotez la trésorerie de votre PME avec un outil adapté : plan prévisionnel, gestion du BFR, alertes et simulation de scénarios de crise."
        keywords="gestion trésorerie PME, outil gestion trésorerie PME, logiciel trésorerie PME, plan de trésorerie PME, BFR, besoin fonds roulement, prévisionnel trésorerie"
        canonical="/gestion-tresorerie-pme"
        jsonLd={faqSchema}
      />

      <div className="min-h-screen bg-white">
        <MarketingHeader
          onNavigate={handleNavigate}
          onSignIn={handleSignIn}
          onSignUp={handleSignUp}
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-24 pb-16 px-4">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Gestion de trésorerie pour PME : méthode, outils et pilotage efficace
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              La <strong>gestion de trésorerie d'une PME</strong> est un enjeu de survie. 25% des PME en bonne santé comptable déposent le bilan faute de liquidités. Face aux délais de paiement clients, aux décalages de charges sociales et aux aléas de l'activité, disposer d'un <strong>outil de pilotage de trésorerie PME</strong> performant est indispensable. Découvrez comment anticiper votre BFR, sécuriser votre cash et piloter sereinement votre croissance.
            </p>
          </div>
        </section>

        {/* Section 1: Définition */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Pourquoi la trésorerie est l'indicateur n°1 d'une PME ?
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              La <strong>trésorerie d'une PME</strong> représente le cash réellement disponible sur vos comptes à un instant T. Elle diffère fondamentalement du résultat comptable ou du chiffre d'affaires facturé.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              Le paradoxe de la PME rentable mais sans cash
            </h3>

            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Une PME peut afficher un résultat positif en fin d'exercice et pourtant ne pas pouvoir payer ses salaires en milieu de mois. Ce paradoxe est la première cause de défaillance des entreprises en croissance.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-8">
              <p className="text-gray-800 font-medium">
                <strong>Exemple concret :</strong> Une PME de BTP signe un chantier de 200 000 €. Elle avance les matériaux (40 000 €) et la main d'oeuvre (30 000 €/mois). Le client paie à 60 jours fin de mois. Résultat : la PME est en cours de réaliser 200 000 € de CA mais doit financer 70 000 € de décaissements avant tout encaissement. Sans visibilité sur ce décalage, le dirigeant peut signer un 2e chantier... et se retrouver en cessation de paiement.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              Spécificités des PME
            </h3>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Délais de paiement clients élevés</strong> : 30 à 90 jours selon les secteurs, parfois davantage avec les grands comptes</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Charges sociales décalées</strong> : cotisations URSSAF trimestrielles ou mensuelles, décalées par rapport aux salaires</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>TVA à avancer</strong> : la TVA collectée doit être reversée avant l'encaissement des créances clients</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Saisonnalité de l'activité</strong> : pics d'activité créant des besoins en trésorerie importants avant les encaissements</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Croissance consommatrice de cash</strong> : chaque nouveau contrat nécessite d'avancer des ressources avant d'encaisser</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Section 2: BFR */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Besoin en Fonds de Roulement (BFR) : le nerf de la guerre
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Le <strong>BFR (Besoin en Fonds de Roulement)</strong> est la somme que votre PME doit financer en permanence pour faire tourner son cycle d'exploitation. C'est l'argent "bloqué" entre le moment où vous payez vos fournisseurs et celui où vos clients vous règlent.
            </p>

            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Formule du BFR</h3>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-center">
                <p className="text-gray-900 text-lg font-bold">BFR = Stocks + Créances clients - Dettes fournisseurs</p>
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Créances clients</p>
                  <p className="text-2xl font-bold text-red-700">+ 45 000 €</p>
                  <p className="text-xs text-gray-500 mt-1">45 jours de CA</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Stocks</p>
                  <p className="text-2xl font-bold text-orange-700">+ 12 000 €</p>
                  <p className="text-xs text-gray-500 mt-1">30 jours d'achats</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Dettes fournisseurs</p>
                  <p className="text-2xl font-bold text-green-700">- 20 000 €</p>
                  <p className="text-xs text-gray-500 mt-1">30 jours d'achats</p>
                </div>
              </div>
              <div className="mt-4 bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-gray-700 font-semibold">BFR = 45 000 + 12 000 - 20 000 = <span className="text-blue-700 text-xl font-bold">37 000 €</span></p>
                <p className="text-xs text-gray-500 mt-1">À financer en permanence pour faire tourner l'activité</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <TrendingUp className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Réduire les délais clients : l'action la plus rapide
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Passer de 60 à 45 jours de délai client sur un CA de 500 000 € libère immédiatement <strong>20 000 € de trésorerie</strong> sans aucun financement externe. Relancer dès J+1 du délai, proposer des acomptes, mettre en place des pénalités de retard : chaque jour gagné améliore votre trésorerie.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <CreditCard className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Négocier les délais fournisseurs
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Passer de 30 à 45 jours fournisseurs réduit votre BFR de 15 jours d'achats. Sur 300 000 € d'achats annuels, c'est <strong>12 000 € de trésorerie libérée</strong>. Négociez ces délais lors du renouvellement des contrats, surtout avec vos principaux fournisseurs.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <BarChart2 className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Optimiser les stocks
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Chaque euro de stock est de la trésorerie immobilisée. Analysez les stocks dormants (plus de 6 mois sans mouvement), mettez en place le juste-à-temps avec vos fournisseurs clés, et optimisez votre point de commande pour réduire les stocks de sécurité sans risquer de rupture.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Problèmes fréquents */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Les 4 situations critiques de trésorerie pour une PME
            </h2>

            <div className="space-y-8">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <AlertCircle className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      1. La croissance rapide
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Paradoxalement, la croissance est un facteur de risque trésorerie. Chaque nouveau contrat ou commande nécessite d'avancer des ressources (matières, main d'oeuvre, sous-traitance) avant d'encaisser. Une PME qui triple son CA en 18 mois peut voir son BFR exploser et se retrouver en défaut de paiement malgré une activité florissante.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Users className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      2. La dépendance à un grand compte
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Quand un client représente plus de 30% du CA, ses délais de paiement imposés (souvent 60 à 90 jours) et ses éventuels retards peuvent mettre en danger toute la trésorerie. Un retard de paiement de 30 jours d'un client à 60 000 €/trimestre crée un manque à gagner immédiat difficile à absorber.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <Calendar className="text-yellow-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      3. Les échéances fiscales et sociales
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      TVA mensuelle ou trimestrielle, URSSAF, IS, CFE, taxe sur les salaires : les échéances fiscales et sociales représentent souvent 20 à 35% du CA annuel. Elles frappent à dates fixes, sans possibilité de décalage, et doivent être provisionnées chaque mois pour éviter les mauvaises surprises.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Zap className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      4. Les investissements non planifiés
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Une machine qui tombe en panne, un véhicule à remplacer, un déménagement imposé : les investissements non prévus au budget peuvent déstabiliser une trésorerie fragile. Sans réserve de sécurité ni simulation préalable, une dépense de 30 000 € peut enclencher une spirale difficile à enrayer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Outils */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Comment gérer la trésorerie d'une PME efficacement ?
            </h2>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <FileText className="text-gray-600" size={24} />
                  Le tableur Excel ou Google Sheets
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2">Avantages :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Gratuit</li>
                      <li>• Totalement personnalisable</li>
                      <li>• Maîtrisé par tous</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-red-700 mb-2">Limites :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Erreurs de formules fréquentes</li>
                      <li>• Mise à jour manuelle chronophage</li>
                      <li>• Pas d'alertes automatiques</li>
                      <li>• Pas de simulation de scénarios</li>
                      <li>• Pas de connexion bancaire</li>
                    </ul>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  Adapté aux TPE avec peu de mouvements. Insuffisant dès que la PME dépasse 500 000 € de CA ou 5 salariés.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <PieChart className="text-gray-600" size={24} />
                  Le logiciel comptable (ERP, Sage, Cegid...)
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2">Avantages :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Vision comptable complète</li>
                      <li>• Suivi des factures et règlements</li>
                      <li>• Connexion bancaire possible</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-red-700 mb-2">Limites :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Vision passée, pas prévisionnelle</li>
                      <li>• Pas de simulation de scénarios</li>
                      <li>• Complexe pour le dirigeant non-comptable</li>
                      <li>• Coût élevé pour les PME</li>
                    </ul>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  Indispensable pour la comptabilité mais insuffisant pour le pilotage prévisionnel de trésorerie.
                </p>
              </div>

              <div className="border-2 border-blue-500 rounded-lg p-6 bg-blue-50">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <Shield className="text-blue-600" size={24} />
                  L'outil spécialisé pilotage trésorerie PME
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2">Avantages :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Plan de trésorerie automatisé sur 12 mois</li>
                      <li>• Alertes préventives avant les tensions</li>
                      <li>• Simulation de scénarios (perte client, retard)</li>
                      <li>• Suivi BFR et indicateurs clés</li>
                      <li>• Rapports pour banquier et associés</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-orange-700 mb-2">À considérer :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Abonnement mensuel</li>
                      <li>• Prise en main initiale (rapide)</li>
                    </ul>
                  </div>
                </div>
                <p className="text-gray-700 font-medium">
                  Solution recommandée pour toute PME avec &gt; 500 000 € de CA ou &gt; 5 collaborateurs.
                </p>
              </div>
            </div>

            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-gray-800 leading-relaxed">
                <strong>Notre constat :</strong> Le tableur Excel convient aux structures légères, mais dès que la PME a plusieurs clients actifs, des charges sociales à provisionner et une saisonnalité marquée, un <strong>logiciel de gestion de trésorerie PME</strong> dédié réduit le risque et libère du temps pour se concentrer sur le développement.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: TrezoOne */}
        <section className="py-16 px-4 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Un outil de trésorerie PME conçu pour anticiper et décider
            </h2>

            <p className="text-xl text-gray-700 mb-10 leading-relaxed">
              TrezoOne est un <strong>outil de pilotage de trésorerie PME</strong> pensé pour les dirigeants et DAF qui veulent une vision claire sans passer par leur expert-comptable. Il automatise les calculs, détecte les tensions à venir et vous permet de tester vos décisions avant de les prendre.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="text-blue-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Plan de trésorerie prévisionnel automatisé
                </h3>
                <p className="text-gray-700">
                  Visualisez votre trésorerie mois par mois sur 12 à 24 mois. Le plan se met à jour automatiquement à chaque opération. Identifiez les mois en tension bien avant qu'ils n'arrivent.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-red-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <AlertCircle className="text-red-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Alertes automatiques préventives
                </h3>
                <p className="text-gray-700">
                  Recevez une alerte 6 à 8 semaines avant un risque de découvert. Réagissez en amont, quand vous avez encore le choix de vos options : relance client, décalage de dépense, financement bancaire.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="text-orange-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Simulation de scénarios
                </h3>
                <p className="text-gray-700">
                  Testez l'impact d'un retard client de 30 jours, d'une perte de contrat, d'un investissement ou d'une embauche avant de décider. La simulation vous montre l'effet sur votre trésorerie à 3, 6 et 12 mois.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <BarChart2 className="text-green-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Indicateurs BFR et FDR
                </h3>
                <p className="text-gray-700">
                  Suivez votre BFR en temps réel et comparez-le à votre fonds de roulement disponible. Anticipez le moment où vous devrez renforcer votre financement.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="text-blue-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Rapports pour banquier et associés
                </h3>
                <p className="text-gray-700">
                  Générez en un clic des rapports de trésorerie prévisionnelle pour vos rendez-vous bancaires ou vos présentations aux associés. Montrez votre maîtrise financière pour faciliter l'accès au crédit.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="text-orange-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Catégorisation automatique des opérations
                </h3>
                <p className="text-gray-700">
                  Les transactions bancaires sont automatiquement catégorisées et intégrées dans le plan de trésorerie. Moins de saisie manuelle, plus de temps pour analyser et décider.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm border-2 border-blue-500">
              <p className="text-lg text-gray-800 leading-relaxed mb-4">
                <strong>Résultat concret :</strong> Nos utilisateurs PME détectent les tensions de trésorerie 6 à 8 semaines à l'avance, contre quelques jours sans outil adapté. Ils réduisent leur délai client moyen de 8 jours en automatisant les relances et gagnent 4 à 6 heures par mois sur le pilotage financier.
              </p>
              <p className="text-gray-700 italic">
                "Avant TrezoOne, je découvrais les problèmes de trésorerie le mois même. Maintenant, je les vois 2 mois à l'avance et je prends mes décisions sereinement. Mon banquier apprécie aussi la qualité de mes prévisions." — Thomas M., Dirigeant PME industrie, 18 salariés
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Cas concrets */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Cas pratiques de gestion de trésorerie en PME
            </h2>

            <div className="space-y-8">
              <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cas n°1 : PME de services B2B, retard d'un grand compte
                </h3>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Contexte :</p>
                  <p className="text-gray-700">
                    PME de 12 personnes, CA de 1,2 M€. Un client grand compte représente 35% du CA (420 000 €/an, soit 35 000 €/mois). Ce client paye à 60 jours. Il prend 30 jours supplémentaires de retard systématique sur un trimestre.
                  </p>
                </div>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Impact trésorerie :</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>Factures en attente :</strong> 3 × 35 000 € = 105 000 € bloqués</li>
                    <li>• <strong>Charges mensuelles :</strong> 75 000 € (salaires + charges + fournisseurs)</li>
                    <li>• <strong>Trésorerie initiale :</strong> 80 000 €</li>
                    <li>• <strong>Mois 1 :</strong> 80 000 - 75 000 + 35 000 (autres clients) = +40 000 €</li>
                    <li>• <strong>Mois 2 :</strong> 40 000 - 75 000 + 35 000 = 0 €</li>
                    <li>• <strong>Mois 3 :</strong> 0 - 75 000 + 35 000 = -40 000 € (découvert)</li>
                  </ul>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800 font-semibold">
                    Sans anticipation : découvert de 40 000 € au mois 3, impossibilité de payer les salaires, agios bancaires, stress pour le dirigeant.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-900 mb-2">Avec TrezoOne :</p>
                  <ul className="space-y-2 text-green-800">
                    <li>• Alerte détectée 6 semaines avant le découvert</li>
                    <li>• Relance formelle du client dès le 1er retard avec mise en demeure</li>
                    <li>• Activation préventive d'une ligne de trésorerie bancaire à taux négocié</li>
                    <li>• Réduction des délais des autres clients (relances anticipées)</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cas n°2 : PME en croissance, embauche de 3 collaborateurs
                </h3>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Contexte :</p>
                  <p className="text-gray-700">
                    PME de distribution, 8 salariés, CA de 900 000 €/an. Opportunité de décrocher 3 nouveaux clients pour +250 000 € de CA. Nécessite d'embaucher 3 commerciaux : coût total charges comprises = 12 000 €/mois. Les nouveaux clients seront actifs dans 3 mois.
                  </p>
                </div>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Analyse avec plan de trésorerie :</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Coût des 3 mois de montée en puissance : 3 × 12 000 € = 36 000 € sans revenus supplémentaires</li>
                    <li>• Trésorerie actuelle : 45 000 €</li>
                    <li>• Après embauche et 3 mois : 45 000 - 36 000 = 9 000 € (marge très faible)</li>
                    <li>• Un retard de 30 jours sur un nouveau client = découvert immédiat</li>
                  </ul>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <p className="text-orange-800 font-semibold">
                    Verdict : croissance possible mais risquée. La marge de sécurité de 9 000 € est insuffisante pour absorber le moindre aléa.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-900 mb-2">Recommandation TrezoOne :</p>
                  <p className="text-blue-800">
                    Négocier avec la banque une ligne de trésorerie de 30 000 € avant l'embauche (la présenter avec le plan prévisionnel). Embaucher en 2 fois : 2 commerciaux maintenant, le 3e à 2 mois. Exiger un acompte de 30% sur les nouveaux contrats.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cas n°3 : Saisonnalité marquée, pic d'activité estival
                </h3>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Contexte :</p>
                  <p className="text-gray-700">
                    PME de l'événementiel, CA de 600 000 €. 70% du CA réalisé entre mai et septembre. Charges salariales stables sur 12 mois : 25 000 €/mois. Les fournisseurs événementiels exigent des paiements à 30 jours. Les clients paient en fin d'événement (encaissement étalé).
                  </p>
                </div>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Simulation trésorerie :</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>Jan-Avr (hors saison) :</strong> Charges 25 000 €/mois, recettes 10 000 €/mois → -15 000 €/mois → -60 000 € sur 4 mois</li>
                    <li>• <strong>Mai-Sept (saison) :</strong> Recettes 84 000 €/mois, charges 55 000 €/mois (fournisseurs inclus) → +29 000 €/mois → +145 000 € sur 5 mois</li>
                    <li>• <strong>Bilan annuel :</strong> excédent de 85 000 €, MAIS point bas de -60 000 € en avril si démarrage avec 0 en trésorerie</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 font-semibold">
                    Sans provisionnement : trésorerie négative de 60 000 € au creux (jan-avr), impossibilité de préparer la saison, risque de ne pas pouvoir honorer les premiers événements.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-900 mb-2">Plan d'action avec TrezoOne :</p>
                  <ul className="space-y-2 text-green-800">
                    <li>• Constituer une réserve de 70 000 € en fin de saison (septembre)</li>
                    <li>• Mettre en place des acomptes de 40% à la signature pour chaque événement</li>
                    <li>• Négocier une ligne saisonnière de 50 000 € auprès de la banque (présentée avec le plan prévisionnel)</li>
                    <li>• Décaler certains recrutements saisonniers en mai plutôt qu'en mars</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 7: Guide pratique */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Comment construire un plan de trésorerie PME en 5 étapes ?
            </h2>

            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Un <strong>plan de trésorerie PME</strong> rigoureux suit une méthodologie en 5 étapes. Voici comment le construire, que vous utilisiez un tableur ou un outil dédié.
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Recenser tous les encaissements prévisionnels
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Listez vos sources de revenus avec leur date d'encaissement réelle (pas de facturation) :
                    </p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Factures clients en cours avec leurs délais de paiement contractuels</li>
                      <li>• Commandes signées ou en cours de signature</li>
                      <li>• Revenus récurrents (abonnements, maintenances)</li>
                      <li>• Crédit d'impôt, remboursements TVA prévus</li>
                    </ul>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                      <p className="text-yellow-800 text-sm">
                        <strong>Règle d'or :</strong> appliquez systématiquement un coefficient de retard de 10 à 15 jours sur les délais clients contractuels. La réalité dépasse souvent les contrats.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-green-500">
                <div className="flex items-start gap-4">
                  <div className="bg-green-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Recenser tous les décaissements avec leurs dates exactes
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Identifiez chaque dépense avec sa date de prélèvement réelle :
                    </p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Salaires et charges salariales (date de virement)</li>
                      <li>• URSSAF (dates de prélèvement mensuelles ou trimestrielles)</li>
                      <li>• TVA (date d'échéance mensuelle ou trimestrielle)</li>
                      <li>• Loyers et charges fixes (dates de prélèvement)</li>
                      <li>• Fournisseurs récurrents (délais négociés)</li>
                      <li>• Remboursements d'emprunts (échéancier)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-orange-500">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Calculer le solde mensuel en cascade
                    </h3>
                    <div className="bg-gray-50 rounded p-4 mb-3 font-mono text-sm">
                      <p className="text-gray-900"><strong>Solde du mois</strong> = Encaissements du mois - Décaissements du mois</p>
                      <p className="text-gray-900 mt-2"><strong>Trésorerie fin de mois</strong> = Trésorerie début mois + Solde du mois</p>
                    </div>
                    <p className="text-gray-700">
                      Répétez ce calcul sur 12 à 24 mois. La trésorerie fin de mois devient la trésorerie début du mois suivant. Si une ligne affiche un solde négatif, c'est une alerte à traiter immédiatement.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-red-500">
                <div className="flex items-start gap-4">
                  <div className="bg-red-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Définir et surveiller votre seuil de sécurité
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Ne vous contentez pas de rester positif. Définissez un <strong>seuil de trésorerie minimal</strong> en dessous duquel vous déclenchez des actions préventives.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                      <p className="font-semibold text-blue-900 mb-2">Règle pratique PME :</p>
                      <p className="text-blue-800 text-sm">
                        Seuil d'alerte = 1,5 à 2 mois de charges fixes
                      </p>
                      <p className="text-blue-700 text-sm mt-2">
                        Exemple : charges fixes de 50 000 €/mois → seuil d'alerte à 75 000 – 100 000 € de trésorerie disponible
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-gray-500">
                <div className="flex items-start gap-4">
                  <div className="bg-gray-700 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    5
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Simuler les scénarios de stress
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Testez votre plan sous différentes hypothèses :
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li>• <strong>Scénario optimiste :</strong> encaissements aux délais contractuels, activité en hausse de 15%</li>
                      <li>• <strong>Scénario nominal :</strong> encaissements avec 10 jours de retard moyen, activité stable</li>
                      <li>• <strong>Scénario pessimiste :</strong> perte d'un client à 20% du CA, retards de 30 jours sur les autres</li>
                    </ul>
                    <p className="text-gray-700 mt-3">
                      Si votre trésorerie reste positive dans le scénario pessimiste, vous êtes résilient. Sinon, identifiez les leviers d'action préventifs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">
                Automatisez votre plan de trésorerie PME avec TrezoOne
              </h3>
              <p className="text-blue-100 mb-6 leading-relaxed">
                Ces 5 étapes sont indispensables mais chronophages à maintenir manuellement chaque mois. TrezoOne les automatise et recalcule votre <strong>plan de trésorerie PME</strong> en temps réel à chaque nouvelle opération.
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
              >
                Tester gratuitement pendant 14 jours
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </section>

        {/* Section 8: FAQ */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Questions fréquentes sur la gestion de trésorerie en PME
            </h2>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Pourquoi la trésorerie est-elle critique pour une PME ?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Une PME peut être rentable sur le papier et pourtant se retrouver en cessation de paiement faute de liquidités. Les délais de paiement clients (30 à 90 jours) créent un décalage entre le moment où vous livrez et celui où vous encaissez. Pendant ce temps, fournisseurs, salariés et charges sociales continuent d'exiger des paiements ponctuels. Selon les statistiques, 25% des défaillances d'entreprises saines comptablement sont liées à des problèmes de trésorerie.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Quelle est la différence entre résultat comptable et trésorerie ?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Le <strong>résultat comptable</strong> mesure la rentabilité : recettes moins dépenses sur une période. La <strong>trésorerie</strong> mesure la liquidité : l'argent réellement disponible en banque à un instant T. Une PME peut afficher un bénéfice de 50 000 € tout en ayant un compte bancaire à zéro si ses clients n\'ont pas encore payé leurs factures. C'est le fameux paradoxe : "je suis rentable mais je n\'ai pas d'argent."
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Comment calculer le BFR d'une PME ?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>BFR = Stocks + Créances clients - Dettes fournisseurs</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Pour une PME de services avec 600 000 € de CA annuel, 45 jours de délai client et 30 jours de délai fournisseur :
                </p>
                <ul className="space-y-1 text-gray-700 mb-3">
                  <li>• Créances clients : 600 000 / 365 × 45 = 74 000 €</li>
                  <li>• Stocks : variable selon activité</li>
                  <li>• Dettes fournisseurs : achats / 365 × 30 = variable</li>
                </ul>
                <p className="text-gray-700">
                  Réduire les délais clients de 15 jours peut libérer 25 000 € de trésorerie sans aucun financement externe.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6 bg-blue-50">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Quel outil de gestion de trésorerie pour une PME ?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Plusieurs options selon la taille et les besoins :
                </p>
                <ul className="space-y-2 text-gray-700 mb-3">
                  <li>• <strong>Excel/Sheets</strong> : gratuit, adapté aux TPE, mais chronophage et sans alertes automatiques</li>
                  <li>• <strong>Logiciel comptable</strong> : vision passée, indispensable pour la compta mais insuffisant pour l'anticipation</li>
                  <li>• <strong>Outil spécialisé</strong> (TrezoOne) : plan prévisionnel automatisé, alertes, simulations de scénarios, rapports</li>
                </ul>
                <p className="text-gray-700 font-medium">
                  Pour toute PME dépassant 500 000 € de CA ou 5 salariés, un <strong>logiciel de trésorerie PME</strong> dédié devient rapidement rentable par le temps gagné et les crises évitées.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Comment améliorer la trésorerie d'une PME rapidement ?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Actions rapides (impact en moins d'un mois) :</strong>
                </p>
                <ul className="space-y-2 text-gray-700 mb-3">
                  <li>• Relancer toutes les factures en retard : peut libérer 15 à 30% du CA en attente</li>
                  <li>• Proposer un escompte de 2% pour paiement à 10 jours sur les prochaines factures</li>
                  <li>• Négocier un décalage de 15 jours sur vos principaux fournisseurs</li>
                  <li>• Mettre en place des acomptes de 30% sur les nouveaux contrats</li>
                </ul>
                <p className="text-gray-700 mb-3">
                  <strong>Actions structurelles (impact en 1 à 3 mois) :</strong>
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Affacturage ou Dailly pour mobiliser les créances clients</li>
                  <li>• Ligne de crédit court terme préventive (à négocier avant le besoin)</li>
                  <li>• Optimisation des stocks (réduction du stock de sécurité)</li>
                  <li>• Mise en place d'un plan de trésorerie prévisionnel automatisé</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section CTA Final */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              Pilotez la trésorerie de votre PME avec visibilité
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              Anticipez les tensions, simulez vos décisions, alertez-vous avant les crises et présentez des prévisions solides à votre banquier. TrezoOne est l'<strong>outil de gestion de trésorerie pour PME</strong> qui vous fait gagner du temps et de la sérénité.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
              <button
                onClick={() => navigate('/auth')}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-2 shadow-lg"
              >
                Tester gratuitement 14 jours
                <ArrowRight size={24} />
              </button>
              <button
                onClick={() => navigate('/tarifs')}
                className="bg-blue-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-400 transition-colors border-2 border-white"
              >
                Voir les tarifs
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">14 jours</div>
                <div className="text-blue-200">d'essai gratuit</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">Sans CB</div>
                <div className="text-blue-200">aucun engagement</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">6 semaines</div>
                <div className="text-blue-200">d'anticipation en moyenne</div>
              </div>
            </div>

            <p className="text-blue-200 mt-10 text-sm">
              Rejoignez les PME qui pilotent sereinement leur trésorerie avec TrezoOne
            </p>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
